import { Box } from '@ff-it/ui';
import cx from 'clsx';
import { AddButton } from 'components';
import { header, oddRow } from 'styles/style.css';
import { fmt } from 'utilities';
import { Row } from './Row';
import {
  amountCell,
  controlCell,
  hiddenWhenStacked,
  stackedBody,
  stackedHead,
  stackedRow,
  table,
  textCell,
} from '../style.css';
import { actionColumn, amountColumn, centeredCell, iconColumn, numberCell, tableCell } from './style.css';
import type { ImportedBlocksLinkerProps, ImportedBlockGroup } from './types';
import { useImportedBlocksLinker } from './useImportedBlocksLinker';

export function ImportedBlocksLinker({ expense, linkerContext }: ImportedBlocksLinkerProps) {
  const { groups, totals, handleLinkAll } = useImportedBlocksLinker({
    expense,
    linkerContext,
  });

  if (groups.length === 0) {
    return null;
  }

  const validGroups = groups.filter((g: ImportedBlockGroup) => g.canAutoLink);
  const invalidGroups = groups.filter((g: ImportedBlockGroup) => !g.canAutoLink);

  return (
    <Box mb="3">
      <table className={table}>
        <colgroup className={hiddenWhenStacked}>
          <col className={iconColumn} />
          <col />
          <col className={actionColumn} />
          <col className={amountColumn} />
          <col className={amountColumn} />
          <col className={actionColumn} />
        </colgroup>
        <thead className={stackedHead}>
          <tr className={cx(stackedRow, header.default)}>
            <td className={cx(centeredCell, hiddenWhenStacked)} />
            <td className={cx(tableCell, textCell)}>Imported blocks</td>
            <td className={hiddenWhenStacked} />
            <td className={cx(numberCell, amountCell)} data-label="Expense">
              Expense
            </td>
            <td className={cx(numberCell, amountCell)} data-label="Balance">
              Balance
            </td>
            <td className={cx(centeredCell, controlCell)} />
          </tr>
        </thead>
        {validGroups.length > 0 && (
          <tbody className={stackedBody}>
            <tr className={cx(stackedRow, header.dark)}>
              <th className={cx(centeredCell, hiddenWhenStacked)} />
              <th className={cx(tableCell, textCell)}>Auto-linkable</th>
              <th className={cx(centeredCell, hiddenWhenStacked)} />
              <th className={cx(numberCell, amountCell)} data-label="Expense">
                {fmt(totals.valid.expenseAmount)}
              </th>
              <th className={cx(numberCell, amountCell)} data-label="Balance">
                {fmt(totals.valid.linkableAmount)}
              </th>
              <th className={cx(centeredCell, controlCell)}>
                <AddButton icon="circle-plus" onClick={handleLinkAll} />
              </th>
            </tr>
            {validGroups.map((group: ImportedBlockGroup, idx: number) => (
              <tr key={group.blockCode} className={cx(stackedRow, idx % 2 === 0 ? undefined : oddRow)}>
                <Row group={group} linkerContext={linkerContext} />
              </tr>
            ))}
          </tbody>
        )}
        {invalidGroups.length > 0 && (
          <tbody className={stackedBody}>
            <tr className={cx(stackedRow, header.dark)}>
              <th className={cx(centeredCell, hiddenWhenStacked)} />
              <th className={cx(tableCell, textCell)}>Manual review required</th>
              <th className={cx(centeredCell, hiddenWhenStacked)} />
              <th className={cx(numberCell, amountCell)} data-label="Expense">
                {fmt(totals.invalid.expenseAmount)}
              </th>
              <th className={cx(numberCell, amountCell)} data-label="Balance">
                {fmt(totals.invalid.linkableAmount)}
              </th>
              <th className={cx(centeredCell, controlCell)} />
            </tr>
            {invalidGroups.map((group: ImportedBlockGroup, idx: number) => (
              <tr key={group.blockCode} className={cx(stackedRow, idx % 2 === 0 ? undefined : oddRow)}>
                <Row group={group} linkerContext={linkerContext} />
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </Box>
  );
}
